/**
 * ConfirmDialog — compact confirmation/alert dialog.
 *
 * SSOT for every confirm-before-acting prompt: delete, archive, remove
 * member, reset settings, discard changes, leave group, etc. Composes
 * `<Modal size="xs">` + two buttons, but hides that composition behind
 * a config-driven API so call-sites read like the design spec:
 *
 *   <ConfirmDialog
 *       open={pending}
 *       title="Delete this project?"
 *       description="All chats + files under this project will be lost."
 *       confirmLabel="Delete project"
 *       destructive
 *       busy={isDeleting}
 *       onConfirm={doDelete}
 *       onClose={() => setPending(false)}
 *   />
 *
 * Replaces every ad-hoc overlay + `window.confirm` across the app.
 */

import { useId } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from './Modal';
import type { ModalMode } from './Modal';
import Button from './Button';
import './ConfirmDialog.css';

export type ConfirmDialogProps = {
    open: boolean;
    onClose: () => void;
    /** Fires when the confirm button is activated. Receives no args;
     *  caller owns the action + any post-close cleanup. */
    onConfirm: () => void;

    /** Title line — keep it short (≤60 chars). */
    title: ReactNode;
    /** Optional body copy. Keep to one or two short sentences. */
    description?: ReactNode;

    /** Confirm-button label. Default 'Confirm'. */
    confirmLabel?: string;
    /** Cancel-button label. Default 'Cancel'. */
    cancelLabel?: string;

    /** Styles the dialog as a destructive action: danger-tinted header,
     *  danger variant on the confirm button, warning icon. Set for
     *  delete / remove / reset flows. */
    destructive?: boolean;

    /** Show a spinner/disable state on the confirm button while the
     *  action is in flight. Also disables backdrop + escape closes
     *  so a mid-flight operation can't be orphaned. */
    busy?: boolean;

    /** When true, the destructive/confirm button is disabled (e.g. waiting
     *  on a typed-name match for delete confirms). Keyboard + screen-reader
     *  users see the disabled state via `disabled` + `aria-disabled`.
     *  Composes with `busy` — either condition disables the button.
     *  Default false. */
    confirmDisabled?: boolean;

    /** Optional extra content rendered between the description and the
     *  footer — e.g. a "Don't show again" checkbox or a "Type DELETE to
     *  confirm" input. Keep it single-purpose; complex forms belong in
     *  a full Modal. */
    children?: ReactNode;

    /** Layout mode forwarded to Modal. Default 'centered'.
     *  Pass `mode={isMobile ? 'sheet-partial' : 'centered'}` at
     *  call-sites that apply §5 mobile sheet rules. */
    mode?: ModalMode;
};

export default function ConfirmDialog({
    open,
    onClose,
    onConfirm,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    busy = false,
    confirmDisabled = false,
    children,
    mode,
}: ConfirmDialogProps) {
    const descId = useId();
    // Single source of truth for the confirm button's disabled state —
    // either an in-flight async (`busy`) or an unmet precondition
    // (`confirmDisabled`, e.g. typed-name mismatch on destructive
    // confirms). Composes both signals so call-sites don't have to.
    const confirmIsDisabled = busy || confirmDisabled;
    return (
        <Modal
            open={open}
            onClose={onClose}
            size="xs"
            tone={destructive ? 'danger' : 'default'}
            ariaLabel={typeof title === 'string' ? title : 'Confirm action'}
            {...(description ? { ariaDescribedBy: descId } : {})}
            closeOnBackdrop={!busy}
            closeOnEscape={!busy}
            {...(mode !== undefined ? { mode } : {})}
        >
            <Modal.Body className="ui-confirm-dialog__body">
                <div className="ui-confirm-dialog__head">
                    {destructive && (
                        <span className="ui-confirm-dialog__icon" aria-hidden="true">
                            <AlertTriangle size={18} />
                        </span>
                    )}
                    <h3 className="ui-confirm-dialog__title">{title}</h3>
                </div>
                {description && (
                    <p id={descId} className="ui-confirm-dialog__desc">{description}</p>
                )}
                {children && (
                    <div className="ui-confirm-dialog__extra">{children}</div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="ghost"
                    size="md"
                    onClick={onClose}
                    disabled={busy}
                >
                    {cancelLabel}
                </Button>
                <Button
                    variant={destructive ? 'danger' : 'primary'}
                    size="md"
                    onClick={onConfirm}
                    disabled={confirmIsDisabled}
                    aria-disabled={confirmIsDisabled}
                >
                    {busy ? 'Working…' : confirmLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
